import { useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowLeft, Plus } from 'lucide-react'
import {
  Button,
  Chip,
  DataTable,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  PartnerPicker,
  Toolbar,
  ToolbarLeft,
} from '@/components/ui'
import type { PickerValue } from '@/components/ui/ProductPicker/ProductPicker'
import { usePriceReviews } from '../api/reviewQueries'
import type { PriceReview, ReviewStatus } from '../api/reviewTypes'
import { NewPriceReviewDrawer } from '../components/NewPriceReviewDrawer'
import { STATUS_META } from '../utils/review-lines'

const STATUS_FILTERS: { value: ReviewStatus | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'submitted', label: 'Needs approval' },
  { value: 'pushed', label: 'Pushed' },
  { value: 'push_failed', label: 'Failed' },
]

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = iso.length === 10 ? new Date(`${iso}T00:00:00`) : new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/** Every customer price review, globally or for one customer (`?partner=`). */
export function PriceReviewsPage() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { partner?: string | number }
  const [partner, setPartner] = useState<PickerValue>(null)
  const [status, setStatus] = useState<ReviewStatus | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const partnerId = partner?.[0] ?? (search.partner ? Number(search.partner) : null)
  // Fetch the whole list and filter here, so the header counts stay true while a chip is selected.
  const { data, isLoading, error, refetch } = usePriceReviews({ partnerId })
  const all = useMemo(() => data?.reviews ?? [], [data])
  const reviews = useMemo(() => (status ? all.filter((r) => r.status === status) : all), [all, status])

  const counts = useMemo(
    () => ({
      draft: all.filter((r) => r.status === 'draft').length,
      submitted: all.filter((r) => r.status === 'submitted').length,
      failed: all.filter((r) => r.status === 'push_failed').length,
    }),
    [all],
  )

  const columns = useMemo<ColumnDef<PriceReview, unknown>[]>(
    () => [
      {
        id: 'customer',
        header: 'Customer',
        cell: ({ row }) => <span className="text-role-data-primary">{row.original.partner_name}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const m = STATUS_META[row.original.status]
          return (
            <Chip size="xs" variant={m.variant}>
              {m.label}
            </Chip>
          )
        },
      },
      { id: 'effective', header: 'Effective', cell: ({ row }) => fmtDate(row.original.effective_date) },
      {
        id: 'changes',
        header: 'Changes',
        cell: ({ row }) =>
          row.original.line_count === undefined
            ? '—'
            : `${row.original.change_count ?? 0} of ${row.original.line_count} lines`,
      },
      { id: 'by', header: 'Submitted by', cell: ({ row }) => row.original.submitted_by ?? '—' },
      { id: 'updated', header: 'Updated', cell: ({ row }) => fmtDate(row.original.updated_at) },
    ],
    [],
  )

  return (
    <div className="flex h-full flex-col">
      <PageHeaderBand
        title="Price reviews"
        description="Per-customer pricing updates: propose new prices, get them approved, push them to the Odoo pricelist."
      >
        <MetricStrip variant="inline">
          <MetricCard variant="inline" label="Drafts" value={counts.draft} />
          <MetricCard variant="inline" label="Needs approval" value={counts.submitted} />
          <MetricCard variant="inline" label="Push failed" value={counts.failed} />
        </MetricStrip>
        <Button variant="secondary" size="sm" onClick={() => void navigate({ to: '/pricelists' } as never)}>
          <ArrowLeft size={14} /> Pricelists
        </Button>
        <Button variant="secondary" size="sm" onClick={() => void navigate({ to: '/pricelists/letters' } as never)}>
          Letters
        </Button>
        <Button variant="primary" size="sm" onClick={() => setDrawerOpen(true)}>
          <Plus size={14} /> New review
        </Button>
      </PageHeaderBand>

      <Toolbar>
        <ToolbarLeft>
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.label}
              size="sm"
              variant={status === f.value ? 'primary' : 'secondary'}
              onClick={() => setStatus(f.value)}
            >
              {f.label}
            </Button>
          ))}
          <div className="w-72">
            <PartnerPicker
              kind="customer"
              variant="field"
              placeholder="Filter by customer…"
              value={partner}
              onChange={setPartner}
            />
          </div>
        </ToolbarLeft>
      </Toolbar>

      <div className="min-h-0 flex-1 bg-canvas p-4">
        <DataTable
          moduleId="price-reviews"
          data={reviews}
          columns={columns}
          stickyFirstColumn={false}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          errorSubject="price reviews"
          getRowId={(r) => r.id}
          onRowClick={(r) =>
            void navigate({ to: '/pricelists/reviews/$reviewId', params: { reviewId: r.id } } as never)
          }
        />
      </div>

      <NewPriceReviewDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  )
}
